/* ===========================================================
   TRM - Live Cupping | the wire

   The format is self-paced. Guests scan once, then taste the
   cups in whatever order they like, answering that cup's
   questions when they are ready. The host opens the tasting,
   lets the room work, then closes it and walks the answers
   cup by cup before the standings.

   One rule shapes this file:

     No answer reaches a phone or the projector before the host
     reveals it. Not as a field, not as ordering, not as a score
     that moves early.

     Self-pacing makes this stricter, not looser: if a guest
     learned their result at cup three, they would tell the room,
     and every score after that would be worthless.

   It is enforced structurally. `Station` and `Question` (which
   know the answers) live only on the server. Everything that can
   travel over a socket is built from the `Public*` shapes, which
   have no answer on them to send.
   =========================================================== */

/* -- language ---------------------------------------------
   TRM's own site is Arabic-first and the audience is Saudi café
   owners. Both languages travel together and the client decides
   which is primary, rather than the server picking for it. */

export type Lang = 'ar' | 'en';

export interface Text {
  ar: string;
  en: string;
}

export const EMPTY_TEXT: Text = { ar: '', en: '' };

/** The primary reading, given the session's language. */
export function primary(text: Text, lang: Lang): string {
  return (lang === 'ar' ? text.ar : text.en) || text.en || text.ar;
}

/** The other one, or '' when there is nothing worth showing twice. */
export function secondary(text: Text, lang: Lang): string {
  const other = lang === 'ar' ? text.en : text.ar;
  return other && other !== primary(text, lang) ? other : '';
}

/* -- the shape of a session --------------------------------
   lobby      guests arriving
   tasting    the room is tasting; nobody is driving
   closed     nothing more can be submitted; the pause
   reveal     the host walks the cups, one at a time
   standings  the top five
   champion   the ending
*/
export type Phase = 'lobby' | 'tasting' | 'closed' | 'reveal' | 'standings' | 'champion';

/* -- questions ---------------------------------------------
   Server-side only. These carry the answers. */

export interface Question {
  id: string;
  prompt: Text;
  /** Two to four. */
  options: Text[];
  correctIndex: number;
}

/** A cup on the table. Guests match the number in front of them. */
export interface Station {
  id: string;
  /** What is printed on the physical cup. */
  number: number;
  /** Optional: what the coffee is called. Blank is fine and common. */
  label: Text;
  /** Two or three. */
  questions: Question[];
  /** The host's private note: what to talk about before revealing. Never leaves the control panel. */
  cue: string;
}

/* -- the only shapes allowed onto a socket ------------------ */

export interface PublicQuestion {
  id: string;
  prompt: Text;
  options: Text[];
}

export interface PublicStation {
  id: string;
  number: number;
  label: Text;
  questions: PublicQuestion[];
}

export function toPublicQuestion(q: Question): PublicQuestion {
  return { id: q.id, prompt: q.prompt, options: q.options.slice() };
}

export function toPublicStation(s: Station): PublicStation {
  return {
    id: s.id,
    number: s.number,
    label: s.label,
    questions: s.questions.map(toPublicQuestion),
  };
}

/* -- scoring -----------------------------------------------
   There is no speed bonus. Over an hour of self-paced tasting
   it would measure nothing but who hurried, and hurrying is the
   opposite of what a cupping asks for. */

export interface ScoreRules {
  correct: number;
  wrong: number;
}

export const DEFAULT_RULES: ScoreRules = { correct: 100, wrong: 0 };

/* -- people ------------------------------------------------ */

export interface PublicPlayer {
  id: string;
  name: string;
  /** Assigned on joining. Two guests may share a name; nobody shares a number. */
  taster: number;
  score: number;
  rank: number;
  /** How many questions this guest has answered. */
  answered: number;
}

export interface BoardRow {
  rank: number;
  name: string;
  taster: number;
  score: number;
  /** True when another name on the same board matches this one, so the
      taster number is worth showing. Otherwise it is clutter. */
  ambiguous: boolean;
}

/* -- the reveal -------------------------------------------- */

export interface DistRow {
  label: Text;
  /** Whole percent. The column always totals exactly 100. */
  pct: number;
  isRight: boolean;
}

export interface QuestionReveal {
  prompt: Text;
  answer: Text;
  hits: number;
  of: number;
  dist: DistRow[];
}

/* -- progress ----------------------------------------------
   How many have finished each cup. Never who, and never what
   they said. */

export interface StationProgress {
  number: number;
  /** Guests who have answered every question on this cup. */
  done: number;
}

/* ===========================================================
   Server -> projector
   =========================================================== */

export interface ProjectorState {
  t: 'projector';
  phase: Phase;
  lang: Lang;
  sessionName: Text;
  code: string;
  joinUrl: string;
  /** How many are in the room. */
  total: number;

  lobby?: {
    names: string[];
    fresh: string[];
    overflow: number;
  };

  /** The cup grid, for as long as the room is tasting. Carries no points. */
  tasting?: {
    stations: StationProgress[];
    /** Questions answered across the whole room, of all there are to answer. */
    answered: number;
    outOf: number;
  };

  /** One cup at a time, once the host starts walking them. */
  reveal?: {
    number: number;
    label: Text;
    index: number;
    outOf: number;
    questions: QuestionReveal[];
  };

  /** The top five. Points are the last thing this screen ever shows. */
  board?: BoardRow[];

  champion?: {
    name: string;
    taster: number;
    score: number;
    rest: BoardRow[];
  };
}

/* ===========================================================
   Server -> phone
   =========================================================== */

/** What this guest has answered on one cup. questionId -> option index. */
export type AnswerMap = Record<string, number>;

export interface PlayerState {
  t: 'player';
  phase: Phase;
  lang: Lang;
  sessionName: Text;
  you: PublicPlayer;

  /** Every cup, so the guest can pick one. Never carries an answer. */
  stations?: PublicStation[];
  /** Station id -> how many of its questions this guest has answered. */
  progress?: Record<string, number>;
  /** This guest's own choices. Presence is the lock. */
  mine?: AnswerMap;

  /** Populated only once the host is revealing, and only for the cup on screen. */
  reveal?: {
    number: number;
    label: Text;
    questions: Array<QuestionReveal & { id: string; mine: number | null; hit: boolean }>;
    /** Points this guest took from this cup. */
    gained: number;
  };

  board?: Array<BoardRow & { isMe: boolean }>;
}

/* ===========================================================
   Server -> control

   The host's screen. The only one that may carry the answers
   and the private cues.
   =========================================================== */

export interface ControlPlayerRow {
  id: string;
  name: string;
  taster: number;
  score: number;
  answered: number;
  bot: boolean;
  offline: boolean;
}

export interface ControlState {
  t: 'control';
  phase: Phase;
  lang: Lang;
  sessionId: string;
  sessionName: Text;
  code: string;
  joinUrl: string;
  total: number;
  /** Questions answered across the room, of all there are to answer. */
  answered: number;
  outOf: number;
  /** The single obvious next action, labelled with what it does. */
  primary: { action: HostAction; label: string; enabled: boolean } | null;
  /** Full content, answers and cues included. */
  stations: Station[];
  progress: StationProgress[];
  /** Which cup is on the projector, while revealing. */
  revealIndex: number;
  players: ControlPlayerRow[];
  demoRunning: boolean;
  rules: ScoreRules;
}

/* ===========================================================
   Clients -> server
   =========================================================== */

export type HostAction = 'openTasting' | 'closeTasting' | 'revealNext' | 'standings' | 'champion';

export type ClientMessage =
  /* phone */
  | { t: 'join'; code: string; name: string; token?: string }
  /** `code` is the session the phone's link names; a token from any other session is refused. */
  | { t: 'resume'; token: string; code?: string }
  /** A phone waking up asks for proof the socket is alive. */
  | { t: 'ping' }
  | { t: 'answer'; questionId: string; option: number }
  /* projector */
  | { t: 'watch'; code: string }
  /* control */
  | { t: 'hostWatch'; key: string }
  | { t: 'host'; key: string; action: HostAction }
  | { t: 'setLang'; key: string; lang: Lang }
  | { t: 'setName'; key: string; name: Text }
  | { t: 'revealAt'; key: string; index: number }
  | { t: 'addGuest'; key: string; name: string }
  | { t: 'removeGuest'; key: string; id: string }
  | { t: 'fillDemo'; key: string; count: number }
  | { t: 'resetScores'; key: string }
  | { t: 'newSession'; key: string }
  /* the content editor - nothing about the cups is hardcoded */
  | { t: 'setStations'; key: string; stations: Station[] }
  | { t: 'addStation'; key: string }
  | { t: 'removeStation'; key: string; id: string };

/* ===========================================================
   Server -> clients (envelopes)
   =========================================================== */

export interface JoinedMessage {
  t: 'joined';
  token: string;
  player: PublicPlayer;
}

export interface ErrorMessage {
  t: 'error';
  code: 'bad-code' | 'bad-name' | 'no-session' | 'stale' | 'not-host' | 'full' | 'bad-content' | 'unknown';
  message: string;
}

export interface NewSessionMessage {
  t: 'newSessionReady';
  key: string;
  code: string;
}

export interface RedirectMessage {
  t: 'redirect';
  code: string;
}

export interface PongMessage {
  t: 'pong';
}

export type ServerMessage =
  | ProjectorState
  | PlayerState
  | ControlState
  | JoinedMessage
  | ErrorMessage
  | NewSessionMessage
  | RedirectMessage
  | PongMessage;

/* -- limits ------------------------------------------------ */

export const MAX_PLAYERS = 200;
export const MAX_NAME_LENGTH = 24;
export const ROSTER_VISIBLE = 25;
/** The top five, as asked for. */
export const BOARD_SIZE = 5;
export const PHONE_BOARD_SIZE = 5;
export const MAX_STATIONS = 12;
export const MAX_QUESTIONS_PER_STATION = 4;
